import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import moment from 'moment';
import {EditorState, genKey, convertFromRaw} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import {useTranslate} from '@computerrock/formation-i18n';
import {matchPath} from '@computerrock/formation-router';
import {FileAssetsDropZone, FileAssetsInput} from '@ace-de/eua-file-assets';
import {alfMessageChannelTypes, alfMessageTypes, alfMessageTextTemplateTypes, alfOutgoingMessageCategoryTypes, etmTaskTypes, etmLeistTaskCategoryTypes, alfInvoiceChannelTypes, languages, persistenceStates} from '@ace-de/eua-entity-types'; // eslint-disable-line max-len
import {Modal, useStyles, ContentBlock, ContentItem, HighlightCircle} from '@ace-de/ui-components';
import {SelectField, Option, InputField, RichTextAreaField} from '@ace-de/ui-components/form';
import {Icon, closeIcon, deleteIcon, infoAlertIcon, fileIcon, uploadIcon, plusIcon} from '@ace-de/ui-components/icons';
import * as communicationActionTypes from '../communicationActionTypes';
import * as serviceCaseSelectors from '../../service-cases/serviceCaseSelectors';
import * as invoiceSelectors from '../../invoices/invoiceSelectors';
import * as taskActionTypes from '../../tasks/taskActionTypes';
import * as contactSelectors from '../../contacts/contactSelectors';
import categoryToTextTemplatesMap from '../categoryToTextTemplatesMap';
import * as userProfileSelectors from '../../user-profiles/userProfileSelectors';
import useSendMessageForm from '../hooks/useSendMessageForm';
import EmailFormFields from '../ui-elements/EmailFormFields';
import MailFormFields from '../ui-elements/MailFormFields';
import CategoryTemplateSection from '../ui-elements/CategoryTemplateSection';
import TaskSection from '../ui-elements/TaskSection';
import styles from './CommunicationModals.module.scss';
import config from '../../config';
import routePaths from '../../routePaths';

const languageOptions = {
    [languages.GERMAN]: 'de-DE',
    [languages.ENGLISH]: 'en-US',
};

const blockStyleFn = contentBlock => {
    if (contentBlock?.type === 'aligned-paragraph') {
        return styles['ace-c-communication-modal__aligned-paragraph'];
    }

    if (contentBlock?.type === 'aligned-paragraph--first-line') {
        return `${styles['ace-c-communication-modal__aligned-paragraph']} ${styles['ace-c-communication-modal__aligned-paragraph--first-line']}`;
    }

    if (contentBlock?.type === 'aligned-paragraph--last-line') {
        return `${styles['ace-c-communication-modal__aligned-paragraph']} ${styles['ace-c-communication-modal__aligned-paragraph--last-line']}`;
    }

    return '';
};

const SendMessageModal = props => {
    const {cx} = useStyles(props, styles);
    const {hasBackdrop, serviceCase, serviceCaseInvoices, accountPartyContactDetails, invoice, senderAccounts} = props;
    const {accountPartyContactDetailsFetchedAt, resetAccountPartyContactDetails} = props;
    const {declineSendMessage, confirmSendMessage, fetchAccountPartyDetails, serviceCaseInvoice} = props;
    const {createTask, activeUser, selectedSearchContact, persistenceState, serviceCaseDocuments, location} = props;
    const {membersByMembershipNo} = props;
    const {createTranslateShorthand, activeLocale, translate} = useTranslate();
    const translateModal = createTranslateShorthand('send_message_modal');

    const memberFromAmp = membersByMembershipNo?.[serviceCase?.member?.membershipNo] || null;
    const currentInvoice = invoice || serviceCaseInvoice;
    const shouldUseAmpMemberData = currentInvoice
        && currentInvoice?.channel
        && currentInvoice.channel !== alfInvoiceChannelTypes.MIA
        && memberFromAmp;

    const form = useSendMessageForm({
        serviceCase,
        accountPartyContactDetails,
        accountPartyContactDetailsFetchedAt,
        fetchAccountPartyDetails,
        resetAccountPartyContactDetails,
        persistenceState,
        invoice,
        selectedSearchContact,
        memberFromAmp,
        shouldUseAmpMemberData,
        serviceCaseInvoices,
        translateOtherLabel: translateModal('recipient_option_label.other'),
    });

    const {
        richTextAreaContent, setRichTextAreaContent,
        selectedRecipient, selectedChannel,
        mailFormData, emailFormData,
        subject, setSubject,
        selectedCategory, setSelectedCategory,
        selectedTemplate, setSelectedTemplate,
        isTaskCreationEnabled, setIsTaskCreationEnabled,
        dueDate, setDueDate,
        dueTime, setDueTime,
        name, setName,
        isDueTimeValid, setIsDueTimeValid,
        setHasEmailError,
        isSendingDisabled, isSendingInProgress,
        recipients,
        handleRecipientSelect, handleChannelSelection,
        handleEmailFormChange, handleMailFormChange,
        buildContactAddress,
        fileList, deleteFile,
        isRecipientDataLoading,
    } = form;

    const [selectedLanguage, setSelectedLanguage] = useState(languageOptions[languages.GERMAN]);
    const [selectedAttachments, setSelectedAttachments] = useState();
    const contactsScreenMatch = matchPath(location?.pathname, {path: routePaths.CONTACTS, exact: true});
    const serviceCaseScreenMatch = matchPath(location?.pathname, {path: routePaths.SERVICE_CASE, exact: true});

    // -- Escape key handler --
    const handleOnKeyDown = useCallback(event => {
        if (event.key === 'Escape' && persistenceState !== persistenceStates.PENDING) declineSendMessage();
    }, [declineSendMessage, persistenceState]);

    useEffect(() => {
        document.addEventListener('keydown', handleOnKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleOnKeyDown, true);
        };
    }, [handleOnKeyDown]);

    // -- Template text formatting (Modal-specific: Draft.js ContentBlocks) --
    const getTextWidth = text => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = getComputedStyle(document.body).font;
        return context.measureText(text).width;
    };

    const getPrice = ({price, currency}) => {
        if (typeof price === 'number' && price > 0) {
            return price.toLocaleString(activeLocale, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
        return '-';
    };

    const createArgumentsArray = (category, template, language) => {
        const {lines, totalInvoicedAmount} = invoice || serviceCaseInvoice || {};
        let alignedText = [];

        const serviceApprovedAmountsText = lines?.map(line => (
            line.approvedAmount
                ? `${translate.apply(this, [
                    `global.service_type.${line.service.toLowerCase()}`,
                    {},
                    language,
                ])}: ${getPrice({
                    price: line.approvedAmount,
                    currency: config.CURRENCY,
                })}` : null
        )).filter(approvedAmount => !!approvedAmount);

        const hasApprovedAmounts = !!serviceApprovedAmountsText?.length;

        if ([alfOutgoingMessageCategoryTypes.REFUND, alfOutgoingMessageCategoryTypes.REPAYMENT].includes(category)
            && lines && hasApprovedAmounts) {
            serviceApprovedAmountsText.push(`${translateModal('text.total_amount')}: ${getPrice({
                price: totalInvoicedAmount,
                currency: config.CURRENCY,
            })}`);

            const MAX_STRING_LENGTH = Math.max(...serviceApprovedAmountsText.map(line => getTextWidth(line)));
            const SPACE_WIDTH = getTextWidth(' ');

            alignedText = [...serviceApprovedAmountsText].map(string => {
                const widthDiff = MAX_STRING_LENGTH - getTextWidth(string);
                const spaceCount = Math.round(widthDiff / SPACE_WIDTH);
                if (!string) return null;
                const [textPart, amount] = string.split(':');
                return `${textPart.trim()}:` + ' '.repeat(spaceCount + config.DEFAULT_SPACE_COUNT) + amount.replace(/\s/g, '');
            });
        }

        switch (category) {
            case alfOutgoingMessageCategoryTypes.OTHER: {
                switch (template) {
                    case alfMessageTextTemplateTypes.REGRESS_INSURANCE: {
                        return {
                            memberName: `${serviceCase?.member?.personalDetails?.displayName || '-'}`,
                            serviceCaseId: `${serviceCase?.prefix}-${serviceCase?.id}`,
                            totalAmount: `${getPrice({price: totalInvoicedAmount, currency: config.CURRENCY})}` || '-',
                        };
                    }
                    case alfMessageTextTemplateTypes.REQUEST_POLICE_OR_PUBLIC_PROSECUTOR_BLOOD_ALCOHOL: {
                        return {
                            memberName: `${serviceCase?.member?.personalDetails?.displayName || '-'}`,
                        };
                    }
                    default: {
                        return {};
                    }
                }
            }
            case alfOutgoingMessageCategoryTypes.REPAYMENT:
            case alfOutgoingMessageCategoryTypes.REFUND: {
                return {
                    serviceApprovedAmounts: alignedText,
                };
            }
            case alfOutgoingMessageCategoryTypes.INVOICE_AND_CREDIT_NOTE: {
                switch (template) {
                    case alfMessageTextTemplateTypes.INVOICE: {
                        return {
                            externalInvoiceNumber: invoice?.externalInvoiceNumber || '-',
                        };
                    }
                    case alfMessageTextTemplateTypes.CREDIT_NOTE: {
                        return {
                            externalInvoiceNumber: invoice?.externalInvoiceNumber || '-',
                            receiptId: invoice?.receiptId || '-',
                            receiptIdVKG: 'xxx-xxxxxxxx',
                            receiptIdPreviousVKR: 'xxx-xxxxxxxx',
                        };
                    }
                    default: {
                        return {};
                    }
                }
            }
            case alfOutgoingMessageCategoryTypes.REQUEST: {
                switch (template) {
                    case alfMessageTextTemplateTypes.INVOICE_CORRECTION: {
                        return {
                            invoiceId: invoice?.id || '-',
                        };
                    }
                    default: {
                        return {};
                    }
                }
            }
            default: {
                return {};
            }
        }
    };

    const formatMessageText = (category, template, language = selectedLanguage) => {
        if (!category || !template) return;

        if (!categoryToTextTemplatesMap[category].includes(template)) {
            setRichTextAreaContent(EditorState.createEmpty());
            return;
        }

        const argsArray = createArgumentsArray(category, template, language);
        let firstParagraph;
        let thirdParagraph;

        if (![
            alfOutgoingMessageCategoryTypes.ELLA,
            alfOutgoingMessageCategoryTypes.OTHER,
            alfOutgoingMessageCategoryTypes.INVOICE_AND_CREDIT_NOTE,
        ].includes(category)
            && !(category === alfOutgoingMessageCategoryTypes.REPAYMENT
                && template === alfMessageTextTemplateTypes.TRUCK_REGISTRATION)
            && !(category === alfOutgoingMessageCategoryTypes.REQUEST
                && template === alfMessageTextTemplateTypes.INVOICE_CORRECTION)) {
            firstParagraph = translateModal.apply(this, [
                `first_paragraph.${category.toLowerCase()}`,
                {...argsArray},
                language,
            ]);

            thirdParagraph = (category === alfOutgoingMessageCategoryTypes.REPAYMENT
                && [alfMessageTextTemplateTypes.GENERAL].includes(template))
                || (category === alfOutgoingMessageCategoryTypes.REQUEST
                    && [alfMessageTextTemplateTypes.INVOICE_CORRECTION].includes(template))
                ? ''
                : translateModal.apply(this, [
                    `third_paragraph.${category.toLowerCase()}`,
                    {...argsArray},
                    language,
                ]);
        }

        const secondParagraph = template !== alfMessageTextTemplateTypes.GENERAL_REFUND
            && !([alfOutgoingMessageCategoryTypes.REQUEST, alfOutgoingMessageCategoryTypes.REJECTION].includes(category)
                && template === alfMessageTextTemplateTypes.GENERAL)
            ? translateModal.apply(this, [
                `second_paragraph.${category.toLowerCase()}_${template.toLowerCase()}`,
                {...argsArray}, language,
            ])
            : '';

        const contentBlocks = [];

        if (firstParagraph) {
            contentBlocks.push({
                key: genKey(),
                type: 'first-paragraph',
                text: `${firstParagraph}${category !== alfOutgoingMessageCategoryTypes.REFUND ? '\n' : ''}`,
                depth: 0,
                inlineStyleRanges: [],
                entityRanges: [],
                data: {},
            });
        }
        if (secondParagraph) {
            const secondParagraphOneLineSpaceFormatted = secondParagraph.replaceAll('&;', '\n');
            contentBlocks.push({
                key: genKey(),
                type: 'second-paragraph',
                text: secondParagraphOneLineSpaceFormatted.replaceAll('&', '\n\n'),
                depth: 0,
                inlineStyleRanges: [],
                entityRanges: [],
                data: {},
            });
        }

        if (argsArray?.serviceApprovedAmounts?.length > 0) {
            argsArray.serviceApprovedAmounts.forEach((amount, index) => {
                contentBlocks.push({
                    key: genKey(),
                    type: `aligned-paragraph${
                        index === 0
                            ? '--first-line'
                            : index === argsArray.serviceApprovedAmounts.length - 1
                                ? '--last-line'
                                : ''}`,
                    text: amount,
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                });
            });
        }

        if (thirdParagraph) {
            contentBlocks.push({
                key: genKey(),
                type: 'third-paragraph',
                text: `${secondParagraph && '\n'}${thirdParagraph.replaceAll('&', '\n\n')}`,
                depth: 0,
                inlineStyleRanges: [],
                entityRanges: [],
                data: {},
            });
        }

        const contentState = convertFromRaw({
            blocks: contentBlocks.sort((a, b) => {
                if ([
                    'aligned-paragraph',
                    'aligned-paragraph--first-line',
                    'aligned-paragraph--last-line',
                ].includes(a.type)
                    && ![
                        'aligned-paragraph',
                        'aligned-paragraph--first-line',
                        'aligned-paragraph--last-line',
                    ].includes(b.type)
                    && category === alfOutgoingMessageCategoryTypes.REFUND) {
                    return contentBlocks.indexOf(b) === 0 ? 1 : -1;
                }

                if ([
                    'aligned-paragraph',
                    'aligned-paragraph--first-line',
                    'aligned-paragraph--last-line',
                ].includes(b.type)
                    && ![
                        'aligned-paragraph',
                        'aligned-paragraph--first-line',
                        'aligned-paragraph--last-line',
                    ].includes(a.type)
                    && category === alfOutgoingMessageCategoryTypes.REFUND) {
                    return contentBlocks.indexOf(a) === 0 ? -1 : 1;
                }

                return 0;
            }),
            entityMap: {},
        });
        setRichTextAreaContent(EditorState.createWithContent(contentState));
    };

    // -- Language change handler (Modal-specific) --
    const handleOnLanguageChange = value => {
        setSelectedLanguage(value);
        if (selectedCategory) setSubject(translate(translateModal.apply(this, [
            `communication_type_option.${selectedCategory.toLowerCase()}`,
            {},
            value,
        ])));
        if (selectedCategory && selectedTemplate) formatMessageText(selectedCategory, selectedTemplate, value);
    };

    // -- Category/Template change handlers --
    const handleCategoryChange = value => {
        setSelectedCategory(value);
        setRichTextAreaContent(EditorState.createEmpty());
        setSubject(translate(translateModal.apply(this, [
            `communication_type_option.${value.toLowerCase()}`,
            {},
            selectedLanguage,
        ])));
        !categoryToTextTemplatesMap[value].includes(selectedTemplate)
            ? setSelectedTemplate('')
            : setSelectedTemplate(selectedTemplate);
    };

    const handleTemplateChange = value => {
        setSelectedTemplate(value);
        formatMessageText(selectedCategory, value);
    };

    // -- Send handler --
    const handleOnMessageSend = () => {
        const htmlMessageContent = stateToHTML(richTextAreaContent.getCurrentContent(), {
            blockRenderers: {
                'aligned-paragraph': block => {
                    const [textPart, amount] = block.getText().split(':');
                    return `<div style="width: 35%; display: flex; align-items: center; justify-content: space-between;">
                                <span>
                                    ${textPart}:
                                </span>
                                <span>
                                    ${amount.trim()}
                                </span>
                            </div>`;
                },
                'aligned-paragraph--first-line': block => {
                    const [textPart, amount] = block.getText().split(':');
                    return `<div style="margin-top: 20px; width: 35%; display: flex; align-items: center; justify-content: space-between;">
                                <span>
                                    ${textPart}:
                                </span>
                                <span>
                                    ${amount.trim()}
                                </span>
                            </div>`;
                },
                'aligned-paragraph--last-line': block => {
                    const [textPart, amount] = block.getText().split(':');
                    return `<div style="margin: 20px 0; width: 35%; display: flex; align-items: center; justify-content: space-between;">
                                <span>
                                    ${textPart}:
                                </span>
                                <span>
                                    ${amount.trim()}
                                </span>
                            </div>`;
                },
            },
        }).replace(/<p>(.*?)<\/p>/g, (match, innerContent) => {
            const trimmedInnerContent = innerContent.trim();
            if (trimmedInnerContent === '<br>') return '<br>';
            return `${trimmedInnerContent}<br>`;
        });

        const contactAddress = buildContactAddress();
        const recipientType = recipients[selectedRecipient]?.recipientType;
        const serviceCaseAttachments = (selectedAttachments && serviceCaseDocuments)
            ? serviceCaseDocuments.filter(scDocument => selectedAttachments.includes(scDocument.id))
            : [];

        const messageData = {
            recipient: name || recipients[selectedRecipient]?.name || '',
            channel: selectedChannel,
            ...(selectedChannel === alfMessageChannelTypes.EMAIL
                ? {
                    toEmail: emailFormData?.toEmail,
                    senderAccountType: emailFormData?.sender,
                }
                : {}
            ),
            ...(contactAddress
                ? {toAddress: contactAddress}
                : {}
            ),
            subject,
            content: htmlMessageContent,
            contactId: recipientType === 'MEMBER'
                ? serviceCase?.member?.membershipNo
                : recipientType === 'PRESELECTED_ACCOUNT_PARTY' || recipientType === 'ACCOUNT_PARTY'
                    ? recipients[selectedRecipient].id
                    : '',
            ...(invoice ? {invoiceId: invoice.id} : {}),
            serviceCaseId: serviceCase?.id,
            messageType: alfMessageTypes.OUTGOING,
            attachments: [...fileList.fileAssets.values()]
                .concat([...serviceCaseAttachments])
                .map(attachment => {
                    return {
                        id: attachment.id,
                        fileName: attachment.fileName,
                        isExistingAttachment: !!serviceCaseAttachments.find(scAttachment => (
                            scAttachment.id === attachment.id
                        )),
                    };
                }),
            language: selectedLanguage === languageOptions[languages.GERMAN] ? 'DE' : 'EN',
            zipFileName: `${invoice?.id ? `${invoice.id}-` : ''}${subject}-${translateModal('file_name_text.attachment')}.zip`,
        };

        confirmSendMessage(messageData);

        if (isTaskCreationEnabled && dueDate && dueTime) {
            let category;

            switch (selectedCategory) {
                case alfOutgoingMessageCategoryTypes.REQUEST: {
                    category = etmLeistTaskCategoryTypes.REQUEST;
                    break;
                }
                case alfOutgoingMessageCategoryTypes.REPAYMENT: {
                    category = etmLeistTaskCategoryTypes.AREAS_OF_CONTRIBUTION;
                    break;
                }
                default: {
                    category = etmLeistTaskCategoryTypes.TASK;
                }
            }

            const taskData = {
                category,
                description: translateModal('task_body.description', {
                    channel: translate(`global.communication_channel_option.${selectedChannel.toLowerCase()}`),
                    addressee: (selectedRecipient === 'OTHER' ? name : recipients[selectedRecipient]?.name) || '',
                    subject,
                }),
                dueDate: moment(`${moment(dueDate).format('YYYY-MM-DD')}T${dueTime}`),
                assignedCaseId: serviceCase?.id,
                memberName: serviceCase?.member?.personalDetails.displayName || '',
                assignedTo: activeUser.id,
                assignedToUser: activeUser.id,
                assignedToRole: false,
                taskType: etmTaskTypes.LEA,
                ...(invoice && {invoiceId: invoice.id}),
            };

            createTask({taskData});
        }
    };

    if (!serviceCase && !selectedSearchContact) return null;

    return (
        <Modal
            hasBackdrop={hasBackdrop}
            contentClassName={cx([
                'ace-c-modal__content--scrollable',
                'global!ace-u-modal-content-size--l',
            ])}
            hasColoredHeader={true}
            title={translateModal('modal_title.send_message')}
            action={(
                <Icon
                    icon={closeIcon}
                    onClick={() => {
                        if (!isSendingInProgress) {
                            declineSendMessage();
                        }
                    }}
                />
            )}
        >
            <ContentBlock className={cx('global!ace-u-full-width')}>
                <ContentItem
                    className={cx([
                        'ace-c-content-item--span-3',
                        'global!ace-u-flex',
                        'global!ace-u-flex--direction-column',
                    ])}
                >
                    <SelectField
                        name="recipient"
                        label={translateModal('select_field_label.recipient')}
                        onChange={handleRecipientSelect}
                        value={selectedRecipient}
                        className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                        isDisabled={isSendingInProgress || isRecipientDataLoading}
                    >
                        {recipients && Object.keys(recipients).filter(key => (
                            selectedChannel !== alfInvoiceChannelTypes.ELLA
                            || (key === invoice?.accountParty?.id)
                        )).sort(key => (
                            recipients[key].id === 'OTHER' ? 1 : -1
                        )).map((recipient, idx) => {
                            return (
                                <Option
                                    name={`recipientOption${idx}`}
                                    value={recipient}
                                    key={recipients[recipient].id}
                                >
                                    {recipients[recipient].name} {recipients[recipient].id && recipients[recipient].id !== 'OTHER' ? ` (${recipients[recipient].id})` : ''}
                                </Option>
                            );
                        })}
                    </SelectField>
                    {selectedRecipient === 'OTHER' && (
                        <InputField
                            name="name"
                            label={`${translateModal('input_field_label.name')}*`}
                            className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                            value={name || ''}
                            onChange={setName}
                            isDisabled={isSendingInProgress}
                        />
                    )}
                    <SelectField
                        className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                        name="channel"
                        label={translateModal('select_field_label.channel')}
                        value={selectedChannel}
                        onChange={value => handleChannelSelection(value, {
                            ellaRecipientId: invoice?.accountParty?.id || '',
                        })}
                        isDisabled={isSendingInProgress}
                    >
                        {Object.values(alfMessageChannelTypes).filter(channel => (
                            channel !== alfMessageChannelTypes.ELLA
                            || (channel === alfMessageChannelTypes.ELLA
                                && invoice?.channel === alfInvoiceChannelTypes.ELLA)
                        )).map(channel => (
                            <Option
                                key={channel}
                                value={channel}
                                name={`channelOption${channel}`}
                            >
                                {translate(`global.communication_channel_option.${channel.toLowerCase()}`)}
                            </Option>
                        ))}
                    </SelectField>
                    {(selectedChannel === alfMessageChannelTypes.EMAIL
                    || selectedChannel === alfMessageChannelTypes.MAIL) && (
                        <SelectField
                            className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                            name="language"
                            label={translateModal('select_field_label.language')}
                            value={selectedLanguage}
                            onChange={value => handleOnLanguageChange(value)}
                            isDisabled={isSendingInProgress}
                        >
                            {Object.keys(languageOptions).map(key => (
                                <Option
                                    key={key}
                                    name={`languageOption${key}`}
                                    value={languageOptions[key]}
                                >
                                    {translateModal(`option_label.${key.toLowerCase()}`)}
                                </Option>
                            ))}
                        </SelectField>
                    )}
                    {selectedChannel === alfMessageChannelTypes.EMAIL && (
                        <EmailFormFields
                            emailFormData={emailFormData}
                            onFieldChange={handleEmailFormChange}
                            senderAccounts={senderAccounts}
                            isDisabled={isSendingInProgress}
                            isToEmailDisabled={isRecipientDataLoading}
                            onEmailValidation={setHasEmailError}
                        />
                    )}
                    {selectedChannel === alfMessageChannelTypes.MAIL && (
                        <MailFormFields
                            mailFormData={mailFormData}
                            onFieldChange={handleMailFormChange}
                            isDisabled={isSendingInProgress || isRecipientDataLoading}
                        />
                    )}
                    <CategoryTemplateSection
                        selectedCategory={selectedCategory}
                        selectedTemplate={selectedTemplate}
                        selectedChannel={selectedChannel}
                        isDisabled={isSendingInProgress}
                        onCategoryChange={handleCategoryChange}
                        onTemplateChange={handleTemplateChange}
                    />
                </ContentItem>
                <ContentItem
                    className={cx([
                        'ace-c-content-item--span-9',
                        'global!ace-u-flex',
                        'global!ace-u-flex--direction-column',
                    ])}
                >
                    <InputField
                        className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                        name="subject"
                        label={translateModal('input_field_label.subject')}
                        value={subject || ''}
                        onChange={setSubject}
                        isDisabled={isSendingInProgress}
                    />
                    <RichTextAreaField
                        name="content"
                        label={translateModal('text_area_field_label.message')}
                        maxLength={config.MESSAGE_MAX_LENGTH}
                        setEditorState={value => {
                            setRichTextAreaContent(value);
                        }}
                        editorState={richTextAreaContent}
                        className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-16')}
                        contentClassName={cx('ace-c-text-area--medium')}
                        locale={activeLocale}
                        isResizable={false}
                        placeholder={translateModal('text_area_field_placeholder.enter_message')}
                        isDisabled={isSendingInProgress}
                        shouldFocus={false}
                        blockStyleFn={blockStyleFn}
                    />
                    {!contactsScreenMatch && !serviceCaseScreenMatch && (
                        <div className={cx('global!ace-u-full-width', 'global!ace-u-grid')}>
                            <SelectField
                                name="attachments"
                                label={translateModal('select_field_label.attachments')}
                                className={cx('global!ace-u-grid-column--span-8')}
                                value={selectedAttachments || ''}
                                onChange={setSelectedAttachments}
                                isMultipleChoice={true}
                                isStrictWidth={true}
                            >
                                {(serviceCaseDocuments || []).map(attachment => (
                                    <Option
                                        key={`${attachment.id}-${attachment.fileName}`}
                                        name={`option-${attachment.id}`}
                                        value={attachment.id}
                                        isSelected={true}
                                        isTruncated={true}
                                    >
                                        { `${attachment.attachmentType
                                            ? `${translate(`global.document_type.${attachment.attachmentType.toLowerCase()}`)} | `
                                            : ''}${attachment.description ? `${attachment.description} | ` : ''}${attachment.fileName}`}
                                    </Option>
                                ))}
                            </SelectField>
                        </div>
                    )}
                    <ContentBlock>
                        <ContentItem className={cx('ace-c-content-item--span-8')}>
                            {selectedChannel !== alfMessageChannelTypes.ELLA && (
                                <div
                                    className={cx([
                                        'global!ace-u-grid',
                                        'global!ace-u-height--full',
                                    ])}
                                >
                                    {[...fileList.fileAssets.values()].length > 0
                                    && (
                                        <div
                                            className={cx([
                                                'global!ace-u-grid-column--span-5',
                                                'ace-c-communication-modal__file-list',
                                            ])}
                                        >
                                            {[...fileList.fileAssets.values()].map((fileAsset, index) => (
                                                <div
                                                    key={fileAsset.id}
                                                    className={cx([
                                                        'global!ace-u-padding--16',
                                                        'global!ace-u-grid',
                                                        'global!ace-u-box-skin--pending',
                                                    ], {
                                                        'global!ace-u-margin--top-16': index !== 0,
                                                    })}
                                                >
                                                    <Icon
                                                        icon={fileIcon}
                                                    />
                                                    <p
                                                        className={cx([
                                                            'global!ace-u-grid-column--span-7',
                                                            'ace-c-communication-modal__file-name',
                                                        ])}
                                                    >
                                                        {fileAsset.fileName}
                                                    </p>
                                                    <Icon
                                                        onClick={() => deleteFile(
                                                            fileAsset,
                                                            config.SHOULD_SEND_DOCUMENT_DELETE_REQUEST,
                                                        )}
                                                        icon={deleteIcon}
                                                        className={cx([
                                                            'ace-c-icon--xs',
                                                            'ace-c-icon--color-medium-emphasis',
                                                        ])}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div
                                        className={cx([
                                            'global!ace-u-width--full',
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--direction-column',
                                        ], {
                                            'global!ace-u-grid-column--span-7': [...fileList.fileAssets.values()].length > 0,
                                            'global!ace-u-grid-column--span-12': [...fileList.fileAssets.values()].length === 0,
                                        })}
                                    >
                                        <FileAssetsDropZone
                                            service="alfFileAssetsService"
                                            fileList="service-case-incoming-messages"
                                            className={cx([
                                                'global!ace-u-padding--40',
                                                'global!ace-u-width--full',
                                                'global!ace-u-height--full',
                                                'global!ace-u-flex',
                                                'global!ace-u-flex--direction-column',
                                                'global!ace-u-flex--justify-center',
                                                'global!ace-u-flex--align-center',
                                            ])}
                                            isDisabled={isSendingInProgress}
                                        >
                                            <HighlightCircle
                                                className={cx([
                                                    'global!ace-u-height--64',
                                                    'global!ace-u-width--64',
                                                    'ace-c-highlight-circle--primary-highlight',
                                                ])}
                                            >
                                                <Icon
                                                    icon={uploadIcon}
                                                    className={cx([
                                                        'ace-c-icon--l',
                                                    ])}
                                                />
                                            </HighlightCircle>
                                            <div
                                                className={cx([
                                                    'global!ace-u-margin--top-16',
                                                    'global!ace-u-flex',
                                                    'global!ace-u-flex--direction-column',
                                                    'global!ace-u-flex--align-center',
                                                ])}
                                            >
                                                <p className={cx('global!ace-u-typography--variant-body-bold')}>
                                                    {translateModal('file_drop_zone_title.document_upload')}
                                                </p>
                                                <FileAssetsInput
                                                    service="alfFileAssetsService"
                                                    fileList="service-case-incoming-messages"
                                                    className={cx([
                                                        'global!ace-u-margin--16-0',
                                                        'ace-c-file-input--no-chrome',
                                                    ])}
                                                    isDisabled={isSendingInProgress}
                                                >
                                                    <div
                                                        className={cx([
                                                            'global!ace-u-flex',
                                                            'global!ace-u-flex--align-center',
                                                            'global!ace-u-flex--justify-center',
                                                            'global!ace-u-cursor--pointer',
                                                        ])}
                                                    >
                                                        <Icon
                                                            icon={plusIcon}
                                                            className={cx([
                                                                'global!ace-u-margin--right-8',
                                                                'ace-c-icon--color-highlight-pressed',
                                                            ])}
                                                        />
                                                        <p
                                                            className={cx([
                                                                'global!ace-u-typography--variant-highlight',
                                                                'global!ace-u-typography--color-higlighted-active',
                                                                'global!ace-u-typography--align-center',
                                                            ])}
                                                        >
                                                            {translateModal('file_drop_zone_label.search_documents')}
                                                        </p>
                                                    </div>
                                                </FileAssetsInput>
                                            </div>
                                        </FileAssetsDropZone>
                                        {fileList.errors.length > 0 && (
                                            <div className={cx('global!ace-u-margin--8-0')}>
                                                {fileList.errors.map((errorInfo, index) => {
                                                    return (
                                                        <div
                                                            key={index}
                                                            className={cx([
                                                                'global!ace-u-flex',
                                                                'global!ace-u-flex--align-center',
                                                                'global!ace-u-margin--4-0',
                                                                'global!ace-u-typography--variant-caption',
                                                                'global!ace-u-typography--color-warning',
                                                            ])}
                                                        >
                                                            <Icon
                                                                icon={infoAlertIcon}
                                                                className={cx([
                                                                    'global!ace-u-margin--right-4',
                                                                    'ace-c-icon--s',
                                                                    'ace-c-icon--color-warning',
                                                                ])}
                                                            />
                                                            {translateModal('upload_error.message')}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </ContentItem>
                        <ContentItem
                            className={cx([
                                'ace-c-content-item--span-4',
                                'global!ace-u-flex',
                                'global!ace-u-flex--direction-column',
                            ])}
                        >
                            <TaskSection
                                isTaskCreationEnabled={isTaskCreationEnabled}
                                setIsTaskCreationEnabled={setIsTaskCreationEnabled}
                                dueDate={dueDate}
                                setDueDate={setDueDate}
                                dueTime={dueTime}
                                setDueTime={setDueTime}
                                isDueTimeValid={isDueTimeValid}
                                setIsDueTimeValid={setIsDueTimeValid}
                                isSendingDisabled={isSendingDisabled}
                                isSendingInProgress={isSendingInProgress}
                                onSend={handleOnMessageSend}
                                areTaskFieldsVisible={!selectedSearchContact?.id}
                            />
                        </ContentItem>
                    </ContentBlock>
                </ContentItem>
            </ContentBlock>
        </Modal>
    );
};

SendMessageModal.propTypes = {
    hasBackdrop: PropTypes.bool,
    serviceCase: PropTypes.object,
    serviceCaseInvoices: PropTypes.arrayOf(PropTypes.object),
    declineSendMessage: PropTypes.func.isRequired,
    confirmSendMessage: PropTypes.func.isRequired,
    fetchAccountPartyDetails: PropTypes.func.isRequired,
    resetAccountPartyContactDetails: PropTypes.func.isRequired,
    createTask: PropTypes.func.isRequired,
    senderAccounts: PropTypes.array,
    accountPartyContactDetails: PropTypes.object,
    accountPartyContactDetailsFetchedAt: PropTypes.number,
    invoice: PropTypes.object,
    serviceCaseInvoice: PropTypes.object,
    activeUser: PropTypes.object,
    selectedSearchContact: PropTypes.object,
    persistenceState: PropTypes.string.isRequired,
    serviceCaseDocuments: PropTypes.array,
    location: PropTypes.object,
    membersByMembershipNo: PropTypes.object,
};

SendMessageModal.defaultProps = {
    hasBackdrop: true,
    serviceCaseInvoices: [],
    senderAccounts: [],
    accountPartyContactDetails: null,
    accountPartyContactDetailsFetchedAt: 0,
    invoice: null,
    serviceCase: null,
    serviceCaseInvoice: null,
    activeUser: null,
    selectedSearchContact: null,
    serviceCaseDocuments: [],
    location: null,
    membersByMembershipNo: null,
};

const mapStateToProps = (state, props) => {
    const getServiceCase = serviceCaseSelectors.createServiceCaseSelector();
    const serviceCaseInvoicesSelector = invoiceSelectors.createServiceCaseInvoicesSelector();
    const createServiceCaseInvoiceSelector = invoiceSelectors.createServiceCaseInvoiceSelector();
    const getInvoice = invoiceSelectors.createInvoicesSelector();
    const getSelectedContact = contactSelectors.createContactSelector();

    return {
        serviceCase: getServiceCase(state, props),
        serviceCaseInvoices: serviceCaseInvoicesSelector(state, props),
        senderAccounts: state.communication.senderAccounts,
        accountPartyContactDetails: state.communication.accountPartyContactDetails,
        accountPartyContactDetailsFetchedAt: state.communication.accountPartyContactDetailsFetchedAt,
        invoice: getInvoice(state, props),
        serviceCaseInvoice: createServiceCaseInvoiceSelector(state, props),
        activeUser: userProfileSelectors.getUser(state),
        selectedSearchContact: getSelectedContact(state, props),
        persistenceState: state.communication.persistenceState,
        serviceCaseDocuments: state.serviceCases.serviceCaseDocuments,
        membersByMembershipNo: state.members.membersByMembershipNo,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        declineSendMessage: payload => {
            dispatch({
                type: communicationActionTypes.DECLINE_SEND_MESSAGE,
                payload,
            });
        },
        confirmSendMessage: payload => {
            dispatch({
                type: communicationActionTypes.CONFIRM_SEND_MESSAGE,
                payload,
            });
        },
        fetchAccountPartyDetails: payload => {
            dispatch({
                type: communicationActionTypes.FETCH_ACCOUNT_PARTY_CONTACT_DETAILS,
                payload,
            });
        },
        resetAccountPartyContactDetails: () => {
            dispatch({
                type: communicationActionTypes.RESET_ACCOUNT_PARTY_CONTACT_DETAILS,
            });
        },
        createTask: payload => {
            dispatch({
                type: taskActionTypes.CREATE_TASK,
                payload,
            });
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SendMessageModal);
